'use client';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

export function isGAEnabled(): boolean {
  return typeof window !== 'undefined' && !!GA_MEASUREMENT_ID && !!window.gtag;
}

// Page view tracking
export function trackPageView(url: string, title?: string): void {
  if (!isGAEnabled()) return;
  window.gtag('event', 'page_view', {
    page_path: url,
    page_title: title,
    send_to: GA_MEASUREMENT_ID,
  });
}

// Generic event tracker
export function trackEvent(
  eventName: string,
  eventParams: Record<string, unknown> = {}
): void {
  if (!isGAEnabled()) return;
  window.gtag('event', eventName, {
    ...eventParams,
    send_to: GA_MEASUREMENT_ID,
  });
}

// ── Conversion Events ──────────────────────────────────────────────────────

/** Contact / quote request form submitted */
export function trackContactFormSubmit(serviceType: string): void {
  trackEvent('generate_lead', {
    event_category: 'Contact',
    event_label: 'Contact Form Submission',
    service_type: serviceType,
    conversion: true,
  });
  trackEvent('contact_form_submit', {
    service_type: serviceType,
  });
}

/**
 * Quote request / pricing CTA clicked.
 * cta_location identifies where the click originated.
 */
export function trackQuoteRequest(
  serviceType: string,
  ctaLocation?: string
): void {
  trackEvent('quote_request', {
    event_category: 'Lead',
    event_label: 'Quote Request',
    service_type: serviceType,
    conversion: true,
    ...(ctaLocation ? { cta_location: ctaLocation } : {}),
  });
}

/**
 * Cleaner application submitted.
 * Accepts experience level, availability days, and submission status.
 */
export function trackCleanerApplication(
  params: {
    experience?: string;
    availability?: string[];
    status?: 'success' | 'error';
  } = {}
): void {
  trackEvent('cleaner_application_submit', {
    event_category: 'Recruitment',
    event_label: 'Cleaner Application',
    conversion: true,
    ...(params.experience ? { experience_level: params.experience } : {}),
    ...(params.availability ? { availability_days: params.availability.join(',') } : {}),
    ...(params.status ? { auth_status: params.status } : {}),
  });
}

/** ROI Calculator interaction */
export function trackROICalculatorInteraction(
  action: 'view' | 'adjust' | 'cta_click',
  params: Record<string, unknown> = {}
): void {
  trackEvent('roi_calculator_interaction', {
    event_category: 'Engagement',
    event_label: `ROI Calculator - ${action}`,
    action,
    ...params,
  });
}

/** Blog post read tracking */
export function trackBlogPostRead(
  slug: string,
  title: string,
  category: string,
  readTime: number
): void {
  trackEvent('blog_post_view', {
    event_category: 'Blog',
    event_label: title,
    post_slug: slug,
    post_category: category,
    read_time_minutes: readTime,
  });
}

/** Blog post scroll depth (25%, 50%, 75%, 100%) */
export function trackBlogScrollDepth(slug: string, depth: number): void {
  trackEvent('blog_scroll_depth', {
    event_category: 'Blog',
    event_label: `${depth}% read`,
    post_slug: slug,
    scroll_depth: depth,
  });
}

/** Lead source attribution from UTM params */
export function trackLeadSource(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');
  if (source || medium || campaign) {
    trackEvent('lead_source_detected', {
      event_category: 'Attribution',
      utm_source: source ?? 'direct',
      utm_medium: medium ?? 'none',
      utm_campaign: campaign ?? 'none',
    });
  }
}

// ── Form Submission Events ─────────────────────────────────────────────────

export type FormSubmitStatus = 'success' | 'error';

/**
 * Generic typed form submission tracker.
 * form_name: contact_form | cleaner_application | quote_request | roi_calculator_cta | owner_signup
 */
export function trackFormSubmit(
  formName: 'contact_form' | 'cleaner_application' | 'quote_request' | 'roi_calculator_cta' | 'owner_signup',
  status: FormSubmitStatus,
  serviceType?: string,
  bedrooms?: string
): void {
  trackEvent('form_submit', {
    event_category: 'Forms',
    form_name: formName,
    status,
    ...(serviceType ? { service_type: serviceType } : {}),
    ...(bedrooms ? { bedrooms } : {}),
  });
}

// ── Email Send Events ──────────────────────────────────────────────────────

export type EmailTemplateName =
  | 'lead_confirmation' |'status_change' |'cleaner_accepted' |'owner_welcome' |'booking_confirmation' |'service_request_update';

export type EmailRecipientType = 'lead' | 'cleaner' | 'owner' | 'admin';

/**
 * Track when a Resend email template is triggered.
 */
export function trackEmailSend(
  templateName: EmailTemplateName,
  recipientType: EmailRecipientType,
  extraParams: Record<string, unknown> = {}
): void {
  trackEvent('email_send', {
    event_category: 'Email',
    template_name: templateName,
    recipient_type: recipientType,
    ...extraParams,
  });
}

// ── Status Change Events ───────────────────────────────────────────────────

export type LeadStatus = 'new' | 'contacted' | 'signed';
export type CleanerAppStatus = 'new' | 'reviewed' | 'hired' | 'rejected';
export type ServiceRequestStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

/**
 * Track admin lead status change (new → contacted → signed).
 */
export function trackLeadStatusChange(
  oldStatus: LeadStatus,
  newStatus: LeadStatus,
  leadId?: string
): void {
  trackEvent('lead_status_change', {
    event_category: 'Admin',
    old_status: oldStatus,
    new_status: newStatus,
    ...(leadId ? { lead_id: leadId } : {}),
  });
}

/**
 * Track admin cleaner application status change.
 */
export function trackCleanerStatusChange(
  oldStatus: CleanerAppStatus,
  newStatus: CleanerAppStatus,
  applicationId?: string
): void {
  trackEvent('cleaner_status_change', {
    event_category: 'Admin',
    old_status: oldStatus,
    new_status: newStatus,
    ...(applicationId ? { application_id: applicationId } : {}),
  });
}

/**
 * Track service request status change (owner dashboard).
 */
export function trackServiceRequestStatusChange(
  oldStatus: ServiceRequestStatus,
  newStatus: ServiceRequestStatus,
  requestId?: string
): void {
  trackEvent('service_request_status_change', {
    event_category: 'Owner',
    old_status: oldStatus,
    new_status: newStatus,
    ...(requestId ? { request_id: requestId } : {}),
  });
}

// ── Auth Flow Events ───────────────────────────────────────────────────────

export type PortalType = 'admin' | 'owner';
export type AuthStatus = 'success' | 'error';

/**
 * Track sign-in attempt.
 * auth_status: 'success' | 'error'
 */
export function trackSignIn(portalType: PortalType, authStatus: AuthStatus = 'success', method: string = 'email'): void {
  trackEvent('sign_in', {
    event_category: 'Auth',
    portal_type: portalType,
    method,
    auth_status: authStatus,
  });
}

/**
 * Track sign-up (owner portal).
 * auth_status: 'success' | 'error'
 */
export function trackSignUp(portalType: PortalType, authStatus: AuthStatus = 'success'): void {
  trackEvent('sign_up', {
    event_category: 'Auth',
    portal_type: portalType,
    method: 'email',
    auth_status: authStatus,
  });
}

/**
 * Track sign-out.
 */
export function trackSignOut(portalType: PortalType): void {
  trackEvent('sign_out', {
    event_category: 'Auth',
    portal_type: portalType,
  });
}

/**
 * Track password reset request (forgot password form submitted).
 */
export function trackPasswordResetRequest(portalType: PortalType): void {
  trackEvent('password_reset_request', {
    event_category: 'Auth',
    portal_type: portalType,
  });
}

/**
 * Track password reset completion (new password saved successfully).
 */
export function trackPasswordResetComplete(portalType: PortalType): void {
  trackEvent('password_reset_complete', {
    event_category: 'Auth',
    portal_type: portalType,
  });
}
